export interface ExportColumn {
  key: string;
  label: string;
  format?: (value: any) => string;
}

function normalizeEpochTimestamp(value: number): number {
  const absolute = Math.abs(value);

  // Accept seconds, milliseconds, microseconds, or nanoseconds.
  if (absolute >= 1e17) return Math.round(value / 1_000_000);
  if (absolute >= 1e14) return Math.round(value / 1_000);
  if (absolute > 0 && absolute < 1e11) return value * 1_000;
  return value;
}

function parseDateValue(value: unknown): Date | null {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value) || value === 0) return null;
    const parsed = new Date(normalizeEpochTimestamp(value));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const numericValue = Number(trimmed);
      if (!Number.isFinite(numericValue) || numericValue === 0) return null;
      const parsedNumeric = new Date(normalizeEpochTimestamp(numericValue));
      return Number.isNaN(parsedNumeric.getTime()) ? null : parsedNumeric;
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

function isDateLikeColumnKey(key: string): boolean {
  const normalized = key.replace(/[^a-z0-9]/gi, "").toLowerCase();

  return (
    normalized.endsWith("date") ||
    normalized.endsWith("timestamp") ||
    normalized.endsWith("createdat") ||
    normalized.endsWith("updatedat") ||
    normalized.endsWith("creationtime")
  );
}

function formatValueForExport(value: unknown, column: ExportColumn): unknown {
  if (column.format) return column.format(value);
  if (value == null) return "";

  if (value instanceof Date) {
    const formattedDate = formatDateForExport(value);
    return formattedDate || value;
  }

  if (isDateLikeColumnKey(column.key)) {
    const formattedDate = formatDateForExport(value);
    if (formattedDate) return formattedDate;
  }

  return value;
}

export function exportToCSV<T extends Record<string, any>>(
  data: T[],
  columns: ExportColumn[],
  filename: string = "export.csv",
): void {
  if (data.length === 0) return;

  const headers = columns.map((col) => col.label);
  const headerRow = headers.map((h) => `"${h}"`).join(",");

  const dataRows = data.map((row) => {
    return columns
      .map((col) => {
        const value = row[col.key];
        const formatted = formatValueForExport(value, col);
        const stringValue = formatted != null ? String(formatted) : "";
        return `"${stringValue.replace(/"/g, '""')}"`;
      })
      .join(",");
  });

  const csv = [headerRow, ...dataRows].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function formatDateForExport(value: unknown): string {
  const date = parseDateValue(value);
  if (!date) return "";

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

export function formatCurrencyForExport(value: number | null): string {
  if (value == null) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
